import { Form, Formik } from "formik";
import { useState } from "react";
import { ArrayFieldInput } from "../../FormFields/ArrayInput/ArrayFieldInput";
import { CheckBoxInputField } from "../../FormFields/CheckBoxInput/CheckBoxInputField";
import ImageUploadField from "../../FormFields/ImageUpload/ImageUpload";
import { TextInputField } from "../../FormFields/TextInput/TextInputField";
import { PropertyType } from "../../../Types/propertyTypes";
import './propertyForm.scss'
import { blankProperty, propertyEligibility, propertySchema } from "./PropertyFormHelpers";
import {
  aboutSectionsFieldValues,
  aliasFieldValues,
  areaUnits,
  propertyTypes,
  blockingLinkedService,
  calendarFieldValues,
  earlyCheckInLinkedService,
  extraFeesFieldValues,
  fontainePortalLinkedService,
  fontainePropertyLinkedService,
  gateCodeLinkedService,
  generateBlankAboutFieldEntry,
  hotelCheckInFee,
  hotelCleaningFee,
  hotelDepositFee,
  hotelResortFee,
  hourSelection,
  lateCheckOutLinkedService,
  listedFieldValues,
  minuteSelection,
  parkingLinkedService,
  regulatoryCategories,
  regulatoryTypes,
  remoteLockLinkedService,
  spaceBathroomFieldValues,
  spaceBedroomFieldValues,
  spaceDiningRoomFieldValues,
} from "./PropertyFormConstants";
import { Button, Col, Row } from "react-bootstrap";
import Collapsible from "../../Collapsible/Collapsible";
import { modImageName } from "../../FormFields/ImageUpload/imageUploadHelpers";
import { imageGalleryFieldValues, linkedServicesFieldValues, registrationRecordsFieldValues } from "../commonFieldValues";
import AddressInputField from "../../FormFields/AddressInput/AddressInputField";
import { DateInputField } from "../../FormFields/DateInput/DateInputField";
import { PhoneNumberInputField } from "../../FormFields/PhoneNumberInput/PhoneNumberInputField";
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField";
import { SelectPropertyModal } from "../../Modals/SelectPropertyModal/selectPropertyModal";
import { FindCoordinatesModal } from "../../Modals/FindCoordinatesModal";
import { checkVal } from "../../../Helpers/Parsers/checkVal";
import merge from 'deepmerge'
import { EditLinkedServiceModal } from "../../Modals/EditLinkedServiceModal/EditLinkedServiceModal";

export const PropertyForm = (props: {
  disabled: boolean,
  onSubmit: (v: any) => Promise<any>,
  uploadImageFn: (v: any) => Promise<string>,
  uploadImageMultiFn: (v: { imageToUpload: any, filename: string }[]) => Promise<string[]>,
  imageUploadProgress: string,
  initialValues?: Partial<PropertyType>,
  propertyOptionList: { value: string, text: string }[]
}) => {

  const {
    disabled,
    onSubmit,
    uploadImageFn,
    uploadImageMultiFn,
    imageUploadProgress,
    initialValues,
    propertyOptionList
  } = props

  const [uploadingImage, setUploadingImage] = useState(false)

  const [copyPropertySettingsModal, setCopyPropertySettingsModal] = useState('')
  const [findCoordinatesModalOpen, setFindCoordinatesModalOpen] = useState(false)
  const [editLinkedServiceModalOpen, setEditLinkedServiceModalOpen] = useState(false)
  const [linkedServiceToEdit, setLinkedServiceToEdit] = useState<any>({
    index: -1,
    payload: {},
    title: '',
  })

  return (
    <div className="property-form">
      <Formik
        initialValues={merge(
          blankProperty(),
          initialValues ? initialValues : {}
        )}
        validationSchema={propertySchema}
        // validate={values => { const errors: any = {}; return errors; }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const submissionResponse = await onSubmit(values)
            if (!submissionResponse.error) {
              return
            }
          } catch (err) {
            // custom error handler
          }
          setSubmitting(false)
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting, validateForm }) => {

          const eligible = propertyEligibility(values)

          return (
            <>
              {/* Scroll to guestInstructionSection */}
              <Button
                onClick={() => {
                  const guestInstructionSection = document.getElementById('guestInstructionSection')
                  if (guestInstructionSection) {
                    guestInstructionSection.scrollIntoView({ behavior: "smooth" })
                  }
                }}
                size={'sm'}
                variant={'secondary'}
              >
                Scroll to Guest Instructions
              </Button>
              <br />

              <Form>

                <h3>ℹ️ General Info</h3>
                <TextInputField
                  fieldLabel="Title"
                  placeholder="title"
                  required={true}
                  fieldName="title" />

                <TextInputField
                  fieldLabel="Description"
                  placeholder="Description"
                  required={true}
                  fieldType={'textarea'}
                  subText={(values.description ? `${values.description.length} characters` : '0 characters') + (values.description && values.description.length < 400 ? ' (description is too short, min is 400 characters)' : '')}
                  fieldName="description" />


                {/* buildingId */}
                <SelectInputField
                  fieldLabel="Property Type"
                  placeholder="- Select Type -"
                  options={propertyTypes}
                  fieldName="type" />

                <TextInputField
                  fieldLabel="Building ID"
                  placeholder="Building ID"
                  // required={true}
                  fieldName="buildingId" />

                {/* <h3>Property Aliases</h3> */}
                <Collapsible
                  title="👤 Property Aliases"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Aliases">Aliases</label><br />
                    <ArrayFieldInput
                      fieldName={'aliases'}
                      values={values.aliases}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ alias: '', aliasSource: '' })}
                      fields={aliasFieldValues} />
                  </div>
                </Collapsible>

                {/* <h3>Owner Information</h3> */}
                <Collapsible
                  title="👑 Owner Information"
                  TitleTag="h3"
                >
                  <div>
                    <Row>
                      <Col xs={12} md={6}>
                        <TextInputField
                          fieldLabel="Owner First Name"
                          placeholder="Owner First Name"
                          fieldName={`owner.firstName`} />
                      </Col>
                      <Col xs={12} md={6}>
                        <TextInputField
                          fieldLabel="Owner Last Name"
                          placeholder="Owner Last Name"
                          fieldName={`owner.lastName`} />
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={12} md={6}>
                        <PhoneNumberInputField
                          fieldLabel="Owner Phone"
                          placeholder="Owner Phone"
                          fieldName={`owner.phone`} />
                      </Col>
                      <Col xs={12} md={6}>
                        <TextInputField
                          fieldLabel="Owner Email"
                          placeholder="Owner Email"
                          fieldName={`owner.email`} />
                      </Col>
                    </Row>
                  </div>
                </Collapsible>

                {/* <h3>Property Registration</h3> */}
                <Collapsible
                  title="📝 Property Registration"
                  TitleTag="h3"
                >
                  <div>
                    <Row>
                      <Col xs={12} md={4}>
                        <SelectInputField
                          fieldLabel="Regulatory Category"
                          placeholder="Regulatory Category"
                          options={regulatoryCategories}
                          fieldName={`registrationInfo.regulatoryCategory`} />
                      </Col>
                      <Col xs={12} md={4}>
                        <SelectInputField
                          fieldLabel="Regulatory Type"
                          placeholder="Regulatory Type"
                          options={regulatoryTypes}
                          fieldName={`registrationInfo.regulatoryType`} />
                      </Col>
                       <Col xs={12} md={4}>
                        <TextInputField
                          fieldLabel="Regulatory Subtype"
                          placeholder="Regulatory Subtype"
                          fieldName={`registrationInfo.regulatorySubtype`} />
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={12} md={12}>
                       <ArrayFieldInput
                          fieldName={'registrationInfo.records'}
                          values={values.registrationInfo.records}
                          setFieldValue={setFieldValue}
                          blankEntryFn={() => ({ recordType: '', value: '', expiration: null })}
                          fields={registrationRecordsFieldValues}
                        />
                      </Col>
                    </Row>
                  </div>
                </Collapsible>

                <h3>📍 Property Address</h3>
                <AddressInputField
                  fieldName={'address'}
                  required={true}
                />

                <h3>🧭 Coordinates</h3>
                <CheckBoxInputField
                  fieldLabel={'Hide exact coordinates from guests'}
                  fieldName={'hideExactCoordinates'} />
                <Row>
                  <Col xs={12} md={5}>
                    <TextInputField
                      fieldLabel="Longitude"
                      placeholder="Longitude"
                      subText="This value should be between -180 and 180"
                      fieldName={`coordinates[0]`} />
                  </Col>
                  <Col xs={12} md={5}>
                    <TextInputField
                      fieldLabel="Latitude"
                      placeholder="Latitude"
                      subText="This value should be between -90 and 90"
                      fieldName={`coordinates[1]`} />
                  </Col>
                  <Col xs={12} md={2}>
                    {/* center in box */}
                    <div className="d-flex justify-content-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mb-3"
                        onClick={() => setFindCoordinatesModalOpen(true)}
                      >
                        🗺️ Find Coordinates
                      </Button>
                    </div>
                  </Col>
                </Row>

                <h3>📐 Area</h3>
                <Row>
                  <Col xs={12} md={6}>
                    <TextInputField
                      fieldLabel="Area Value"
                      placeholder="Area Value"
                      fieldType="number"
                      required={true}
                      subText="This value should be a positive number"
                      onChange={(e: any) => {
                        const value = parseFloat(e.target.value)
                        if (!isNaN(value) && value > 0) {
                          setFieldValue('area.unit', 'sqft')
                        } else {
                          setFieldValue('area.unit', '')
                        }
                      }}
                      fieldName={`area.value`} />
                  </Col>
                  <Col xs={12} md={6}>
                    <SelectInputField
                      fieldLabel="Area Unit"
                      placeholder="Area Unit"
                      required={true}
                      options={areaUnits}
                      fieldName={`area.unit`} />
                  </Col>
                </Row>

                <h3>📷 Featured Image</h3>
                {values.featuredImage && (
                  <>
                    <img alt={'featured preview'} src={values.featuredImage} className="image-preview" />
                    <br />
                  </>
                )}
                <TextInputField
                  fieldLabel="Featured Image"
                  placeholder="Featured Image"
                  fieldName={`featuredImage`} />

                <TextInputField
                  fieldLabel="Thumbnail Image"
                  placeholder="Thumbnail Image"
                  fieldName={`thumbnailImage`} />

                <ImageUploadField
                  key={'featuredImageUpload'}
                  uploadedImageCallback={async (imageInfo: any, thumbInfo: any) => {
                    setUploadingImage(true)
                    try {
                      const uploadedImageUrl = await uploadImageFn(imageInfo)
                      const uploadedThumbUrl = await uploadImageFn({
                        ...thumbInfo,
                        filename: modImageName(thumbInfo.filename, '_thumb')
                      })
                      if (uploadedImageUrl) {
                        setFieldValue('featuredImage', uploadedImageUrl)
                        setFieldValue('thumbnailImage', uploadedThumbUrl)
                      }
                    } catch (err) {
                      // something went wrong
                    }
                    setUploadingImage(false)
                  }}
                />
                <br />
                {/*<ErrorMessage name="featuredImage" component="div" />*/}

                {/* <h3>PropertyImages</h3> */}
                <Collapsible
                  title="🖼️ Property Images"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Image Gallery">Image Gallery</label><br />
                    <ArrayFieldInput
                      fieldName={'imageGallery'}
                      values={values.imageGallery}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ url: '', title: '', description: '', thumbUrl: '' })}
                      imgPreview={{
                        srcVal: 'url'
                      }}
                      fields={imageGalleryFieldValues} />
                    {imageUploadProgress && (<>{imageUploadProgress} <br /></>)}
                    <ImageUploadField
                      key={'imageGalleryUpload'}
                      multipleImages={true}
                      uploadedImageCallback={async (imagesInfo: any[], thumbsInfo: any[]) => {
                        setUploadingImage(true)
                        try {
                          const uploadedImageList = await uploadImageMultiFn(imagesInfo)
                          const uploadedThumbs = await uploadImageMultiFn(thumbsInfo.map(x => {
                            return {
                              ...x,
                              filename: modImageName(x.filename, '_thumb')
                            }
                          }))
                          if (uploadedImageList) {
                            const mappedUploadedImages = uploadedImageList.map((imgUrl: string, idx: number) => {
                              const thumbUrl = uploadedThumbs && uploadedThumbs[idx] ? uploadedThumbs[idx] : ''
                              return {
                                url: imgUrl,
                                title: '',
                                description: '',
                                thumbUrl
                              }
                            })
                            setFieldValue('imageGallery', [...values.imageGallery, ...mappedUploadedImages])
                          }
                        } catch (err) {
                          // something went wrong
                        }
                        setUploadingImage(false)
                      }}
                    />
                  </div>
                </Collapsible>

                {/* <h3>Calendars</h3> */}
                <Collapsible
                  title="🗓️ Calendars"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Calendars">Calendars</label><br />
                    <ArrayFieldInput
                      fieldName={'calendars'}
                      values={values.calendars}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ url: '', source: '' })}
                      fields={calendarFieldValues} />
                  </div>
                </Collapsible>


                {/* <h3>Listed</h3> */}
                <Collapsible
                  title="📋 Listed"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Listed">Listed</label><br />
                    <ArrayFieldInput
                      fieldName={'listed'}
                      values={values.listed}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ url: '', source: '', internalName: '', internalTitle: '' })}
                      fields={listedFieldValues} />
                  </div>
                </Collapsible>

                <h3>📇 Property Details</h3>
                <TextInputField
                  fieldLabel="Summary"
                  placeholder="Summary"
                  fieldType={'textarea'}
                  fieldName={`about.summary`} />


                <h3>🫂 Guests</h3>
                <TextInputField
                  fieldType="number"
                  fieldLabel="Guests (Total)"
                  required={true}
                  placeholder="Guests"
                  fieldName={`about.guests`} />
                <Row>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Adults"
                      placeholder="Adults"
                      fieldName={`about.guestInfo.adults`} />
                  </Col>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Children"
                      placeholder="Children"
                      fieldName={`about.guestInfo.children`} />
                  </Col>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Infants"
                      placeholder="Infants"
                      fieldName={`about.guestInfo.infants`} />
                  </Col>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Pets"
                      placeholder="Pets"
                      fieldName={`about.guestInfo.pets`} />
                  </Col>
                </Row>



                <h3>🏠 Rooms and Baths</h3>

                <Row>
                  <Col xs={12} md={4}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Bedrooms"
                      required={true}
                      placeholder="Bedrooms"
                      fieldName={`about.bedrooms`} />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Beds"
                      required={true}
                      placeholder="Beds"
                      fieldName={`about.beds`} />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Other Beds"
                      required={true}
                      placeholder="Other Beds (sofabeds, hammocks, etc...)"
                      fieldName={`about.otherBeds`} />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={4}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Bathrooms"
                      required={true}
                      placeholder="Bathrooms"
                      fieldName={`about.bathrooms`} />

                  </Col>
                  <Col xs={12} md={4}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Baths"
                      required={true}
                      placeholder="Baths"
                      fieldName={`about.baths`} />
                  </Col>
                  <Col xs={12} md={4}>

                    <TextInputField
                      fieldType="number"
                      fieldLabel="Half Bathrooms"
                      required={true}
                      placeholder="Half Bathrooms"
                      fieldName={`about.halfBathrooms`} />
                  </Col>
                </Row>




                <Collapsible title="🚿 Bathrooms" TitleTag="h3">
                  <div>
                    <ArrayFieldInput
                      fieldName={'spaces.bathrooms'}
                      values={values.spaces?.bathrooms ?? []}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ title: '', description: '', bathroomType: '', amenities: [] })}
                      fields={spaceBathroomFieldValues}
                    />
                  </div>
                </Collapsible>

                <Collapsible title="🛏️ Bedrooms" TitleTag="h3">
                  <div>
                    <ArrayFieldInput
                      fieldName={'spaces.bedrooms'}
                      values={values.spaces?.bedrooms ?? []}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ title: '', description: '', beds: [] })}
                      fields={spaceBedroomFieldValues}
                    />
                  </div>
                </Collapsible>

                <Collapsible title="🛋️ Living Rooms" TitleTag="h3">
                  <div>
                    <ArrayFieldInput
                      fieldName={'spaces.livingRooms'}
                      values={values.spaces?.livingRooms ?? []}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ title: '', description: '', beds: [] })}
                      fields={spaceBedroomFieldValues}
                    />
                  </div>
                </Collapsible>

                <Collapsible title="💼 Offices" TitleTag="h3">
                  <div>
                    <ArrayFieldInput
                      fieldName={'spaces.offices'}
                      values={values.spaces?.offices ?? []}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ title: '', description: '', beds: [] })}
                      fields={spaceBedroomFieldValues}
                    />
                  </div>
                </Collapsible>

                <Collapsible title="🍽️ Dining Rooms" TitleTag="h3">
                  <div>
                    <ArrayFieldInput
                      fieldName={'spaces.diningRooms'}
                      values={values.spaces?.diningRooms ?? []}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ title: '', description: '', capacity: 0 })}
                      fields={spaceDiningRoomFieldValues}
                    />
                  </div>
                </Collapsible>

                <label htmlFor="Amenities">⚙️ Amenities</label><br />
                UPDATE AMENITIES COMPONENT<br /><br />
                {/*<SimpleArrayFieldInput
              fieldName={'about.amenities'}
              values={values.about.amenities}
              possibleValues={[
                { value: 'bbq', text: 'BBQ' },
                { value: 'microwave', text: 'Microwave' },
                { value: 'heatedTub', text: 'Heated Tub' },
                { value: 'poolTable', text: 'Pool Table' },
              ]}
            /><br />*/}
                {/* <h4>Description Sections</h4> */}
                <Collapsible
                  title="ℹ️ Description Sections"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Description Sections">Description Sections</label><br />

                    {[
                      { sectionId: 'the-space', sectionTitle: 'The Space' },
                      { sectionId: 'included-with-stay', sectionTitle: 'Included with Stay' },
                      { sectionId: 'hospitality', sectionTitle: 'Hospitality' },
                      { sectionId: 'arrival-checkin', sectionTitle: 'Arrival & Check-In' },
                      { sectionId: 'fees-policies', sectionTitle: 'Fees & Policies' },
                      { sectionId: 'additional-cleaning', sectionTitle: 'Additional Cleaning' },
                      { sectionId: 'towel-linen-supply', sectionTitle: 'Towel & Linen Supply' },
                      { sectionId: 'pack-play', sectionTitle: 'Pack & Play' },
                      { sectionId: 'additional-bed', sectionTitle: 'Additional Bed' },
                      { sectionId: 'dining', sectionTitle: 'Dining' },
                      { sectionId: 'maintenance', sectionTitle: 'Maintenance' },
                      { sectionId: 'experience', sectionTitle: 'Experience' },
                      { sectionId: 'about-building', sectionTitle: 'About the Building' },
                      { sectionId: 'important-notes', sectionTitle: 'Important Notes' },
                      { sectionId: 'ownership-opportunity', sectionTitle: 'Ownership Opportunity' },
                    ].map((section, idx) => {
                      const alreadyExists = values.about.sections.some((s: any) => s.sectionId === section.sectionId)
                      if (alreadyExists) {
                        return null
                      }
                      return (
                        <Button
                          key={`add-section-btn-${idx}`}
                          className={"me-2 mb-2"}
                          variant={"secondary"}
                          size={"sm"}
                          onClick={() => {
                            setFieldValue(
                              'about.sections',
                              [
                                ...values.about.sections,
                                { ...generateBlankAboutFieldEntry(), sectionId: section.sectionId, sectionTitle: section.sectionTitle }
                              ]
                            )
                          }}>
                          + {section.sectionTitle}
                        </Button>
                      )
                    })}

                    {values?.about?.sections?.length === 0 && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"dark"}
                        size={"sm"}
                        onClick={() => {
                          setCopyPropertySettingsModal('about.sections')
                        }}>
                        Copy Other Property
                      </Button>
                    )}

                    <ArrayFieldInput
                      fieldName={'about.sections'}
                      values={values.about.sections}
                      setFieldValue={setFieldValue}
                      blankEntryFn={generateBlankAboutFieldEntry}
                      uploadImageFn={uploadImageFn}
                      imgPreview={{
                        srcVal: 'sectionImage'
                      }}
                      fields={aboutSectionsFieldValues} />
                  </div>
                </Collapsible>

                <div
                  id="guestInstructionSection"
                  style={{
                    scrollMarginTop: '100px'
                  }}
                />


                <Collapsible
                  title="🙋 Guest Instructions"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Guest Instructions">Guest Instructions</label><br />

                    {/* begin guest instruction buttons */}

                    {[
                      { sectionId: 'general', sectionTitle: 'General', content: '' },
                      { sectionId: 'amenities', sectionTitle: 'Amenities', content: '' },
                      { sectionId: 'additionalFees', sectionTitle: 'Additional Fees', content: '' },
                      { sectionId: 'lateCheckInFee', sectionTitle: 'Late Check In Fee', content: 'After 10PM an extra $50 fee\nAfter 12AM an extra  $100 fee' },
                      { sectionId: 'lock', sectionTitle: 'Lock', content: '' },
                      { sectionId: 'internet', sectionTitle: 'Internet', content: `WIFI NAME: XXXXXXXX\nWIFI PASSWORD: ZZZZZZZZ` },
                      { sectionId: 'parking', sectionTitle: 'Parking', content: '' },
                      { sectionId: 'luggageStorage', sectionTitle: 'Luggage Storage', content: 'We have partnered with Bounce. A luggage storage facility in the area that can hold your luggage for you.\nYou may use our link below to gain a 5% discount off your luggage storage.\nhttps://bounce.com/s/location/08712ce3-eda4-43ba-83cc-7d76e3d00081?ref=BENICHAYBROTHERS&coupon=BENICHAYBROTHERS' },
                      { sectionId: 'packageDelivery', sectionTitle: 'Package Delivery', content: 'We do not hold packages for liability reasons. We recommend using a package receiving company in the area that will hold your packages until you arrive. Here is a link to use this service through Bounce.\nhttps://bounce.com/packages/location?query=Miami%20Beach%2C%20FL%2C%20USA' },
                    ].map((guestInstruction: any, idx: number) => {
                      let isEligible = eligible?.guestInstructions?.[guestInstruction.sectionId]
                      if (!isEligible) {
                        return null
                      }
                      return (
                        <Button
                          key={`add-guest-instruction-btn-${idx}`}
                          className={"me-2 mb-2"}
                          variant={"secondary"}
                          size={"sm"}
                          onClick={() => {
                            setFieldValue(
                              'about.guestInstructions',
                              [
                                ...values.about.guestInstructions,
                                guestInstruction
                              ]
                            )
                          }}>
                          + {guestInstruction.sectionTitle}
                        </Button>
                      )
                    })}

                    {values?.about?.guestInstructions?.length === 0 && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"dark"}
                        size={"sm"}
                        onClick={() => {
                          setCopyPropertySettingsModal('about.guestInstructions')
                        }}>
                        Copy Other Property
                      </Button>
                    )}

                    {/* end guest instruction buttons */}

                    <ArrayFieldInput
                      fieldName={'about.guestInstructions'}
                      values={values.about.guestInstructions}
                      setFieldValue={setFieldValue}
                      blankEntryFn={generateBlankAboutFieldEntry}
                      uploadImageFn={uploadImageFn}
                      imgPreview={{
                        srcVal: 'sectionImage'
                      }}
                      fields={aboutSectionsFieldValues} />
                  </div>
                </Collapsible>


                <Collapsible
                  title="🚪 Check Out Instructions"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Guest Instructions">Check Out Instructions</label><br />

                    {[
                      { sectionId: 'trash', sectionTitle: 'Throw trash away', content: 'Please place trash in outside bins.' },
                      { sectionId: 'turnThingsOff', sectionTitle: 'Turn Things Off', content: 'Please turn off all lights and set thermostat to 74 degrees.' },
                      { sectionId: 'lockUp', sectionTitle: 'Lock Up', content: 'Close the door behind you.' },
                      { sectionId: 'returnKeys', sectionTitle: 'Return Keys', content: 'Return key cards to front desk' },
                      { sectionId: 'thermoStat', sectionTitle: 'Thermo Stat', content: 'Set thermostat to 74 degrees.' },
                      { sectionId: 'additionalRequests', sectionTitle: 'Additional Requests', content: '' },
                    ].map((guestInstruction: any, idx: number) => {
                      let isEligible = eligible?.guestCheckOutInstructions?.[guestInstruction.sectionId]
                      if (!isEligible) {
                        return null
                      }
                      return (
                        <Button
                          key={`add-guest-checkout-instruction-btn-${idx}`}
                          className={"me-2 mb-2"}
                          variant={"secondary"}
                          size={"sm"}
                          onClick={() => {
                            setFieldValue(
                              'about.guestCheckOutInstructions',
                              [
                                ...values.about.guestCheckOutInstructions,
                                guestInstruction
                              ]
                            )
                          }}>
                          + {guestInstruction.sectionTitle}
                        </Button>
                      )
                    })}

                    {values?.about?.guestCheckOutInstructions.length === 0 && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"dark"}
                        size={"sm"}
                        onClick={() => {
                          setCopyPropertySettingsModal('about.guestCheckOutInstructions')
                        }}>
                        Copy Other Property
                      </Button>
                    )}

                    {/* end guest instruction buttons */}

                    <ArrayFieldInput
                      fieldName={'about.guestCheckOutInstructions'}
                      values={values.about.guestCheckOutInstructions}
                      setFieldValue={setFieldValue}
                      blankEntryFn={generateBlankAboutFieldEntry}
                      uploadImageFn={uploadImageFn}
                      imgPreview={{
                        srcVal: 'sectionImage'
                      }}
                      fields={aboutSectionsFieldValues} />
                  </div>
                </Collapsible>

                {/* registrationMessage field textarea */}
                <TextInputField
                  fieldType="textarea"
                  fieldLabel="📝 Registration Message"
                  placeholder="Registration Message"
                  fieldName={`about.registrationMessage`} />

                <Row className="mb-2">
                  <Col>
                    <h3>🕓 Check In / Check Out</h3>
                  </Col>
                  <Col xs="auto">
                    <Button
                      size="sm"
                      onClick={() => {
                        setFieldValue('about.checkInHour', 16)
                        setFieldValue('about.checkInMinute', 0)
                        setFieldValue('about.checkOutHour', 11)
                        setFieldValue('about.checkOutMinute', 0)
                        setTimeout(() => {
                          validateForm()
                        }, 300)
                      }}
                    >
                      Use Standard Times
                    </Button>
                  </Col>

                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <SelectInputField
                      fieldLabel={'Check In Hour'}
                      fieldName={'about.checkInHour'}
                      required={true}
                      placeholder="- Select Hour -"
                      options={hourSelection} />
                  </Col>
                  <Col xs={12} md={6}>
                    <SelectInputField
                      fieldLabel={'Check In Minute'}
                      fieldName={'about.checkInMinute'}
                      required={true}
                      placeholder="- Select Minute -"
                      options={minuteSelection} />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6}>
                    <SelectInputField
                      fieldLabel={'Check Out Hour'}
                      fieldName={'about.checkOutHour'}
                      required={true}
                      placeholder="- Select Hour -"
                      options={hourSelection} />
                  </Col>
                  <Col xs={12} md={6}>
                    <SelectInputField
                      fieldLabel={'Check Out Minute'}
                      fieldName={'about.checkOutMinute'}
                      required={true}
                      placeholder="- Select Minute -"
                      options={minuteSelection} />
                  </Col>
                </Row>


                <h3>🏘️ Multi Unit</h3>
                <CheckBoxInputField
                  fieldLabel={'Is this a multi unit?'}
                  fieldName={'multiUnit.isMultiUnit'} />

                {values.multiUnit.isMultiUnit && (
                  <>
                    <label htmlFor="Linked Units">Linked Units</label><br />
                    <ArrayFieldInput
                      fieldName={'multiUnit.linkedUnits'}
                      values={values.multiUnit.linkedUnits}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ propertyId: '', relationship: '' })}
                      fields={[
                        {
                          fieldType: 'select',
                          fieldLabel: 'Property Id',
                          placeholder: '- Select Property Id -',
                          fieldName: 'propertyId',
                          options: propertyOptionList
                        },
                        {
                          fieldType: 'select',
                          fieldLabel: 'Relationship',
                          placeholder: '- Select Relationship -',
                          fieldName: 'relationship',
                          options: [
                            { value: 'child', text: 'Child' },
                            { value: 'parent', text: 'Parent' },
                          ]
                        },
                      ]} />
                  </>
                )}

                <h3>🏷️ Pricing</h3>
                <Row>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Base Rate"
                      required={true}
                      placeholder="Base Rate"
                      fieldName={`pricing.baseRate`} />
                  </Col>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Deposit"
                      required={true}
                      placeholder="Deposit"
                      fieldName={`pricing.deposit`} />
                  </Col>

                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Tax %"
                      required={true}
                      placeholder="Tax %"
                      fieldName={`pricing.tax`} />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Cleaning"
                      required={true}
                      placeholder="Cleaning"
                      fieldName={`pricing.cleaning`} />
                  </Col>
                  <Col xs={12} md={3}>
                    <TextInputField
                      fieldType="number"
                      fieldLabel="Basic Cleaning"
                      required={true}
                      placeholder="Basic Cleaning"
                      fieldName={`pricing.basicCleaning`} />
                  </Col>
                </Row>

                {/* <h4>Extra Fees</h4> */}
                <Collapsible
                  title="💸 Extra Fees"
                  TitleTag={'h3'}
                >
                  <div>
                    <label htmlFor="Extra Fees">Extra Fees</label><br />

                    {eligible?.extraFees?.hotelCleaning && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"secondary"}
                        size={"sm"}
                        onClick={() => {
                          setFieldValue(
                            'pricing.extraFees',
                            [
                              ...values.pricing.extraFees,
                              hotelCleaningFee()
                            ]
                          )
                        }}>
                        + Hotel Cleaning
                      </Button>
                    )}
                    {eligible?.extraFees?.hotelDeposit && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"secondary"}
                        size={"sm"}
                        onClick={() => {
                          setFieldValue(
                            'pricing.extraFees',
                            [
                              ...values.pricing.extraFees,
                              hotelDepositFee()
                            ]
                          )
                        }}>
                        + Hotel Deposit
                      </Button>
                    )}
                    {eligible?.extraFees?.hotelResort && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"secondary"}
                        size={"sm"}
                        onClick={() => {
                          setFieldValue(
                            'pricing.extraFees',
                            [
                              ...values.pricing.extraFees,
                              hotelResortFee()
                            ]
                          )
                        }}>
                        + Hotel Resort
                      </Button>
                    )}
                    {eligible?.extraFees?.hotelCheckIn && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"secondary"}
                        size={"sm"}
                        onClick={() => {
                          setFieldValue(
                            'pricing.extraFees',
                            [
                              ...values.pricing.extraFees,
                              hotelCheckInFee()
                            ]
                          )
                        }}>
                        + Hotel Check-In
                      </Button>
                    )}

                    {values?.pricing?.extraFees?.length === 0 && (
                      <Button
                        className={"me-2 mb-2"}
                        variant={"dark"}
                        size={"sm"}
                        onClick={() => {
                          setCopyPropertySettingsModal('pricing.extraFees')
                        }}>
                        Copy Other Property
                      </Button>
                    )}

                    <ArrayFieldInput
                      fieldName={'pricing.extraFees'}
                      values={values.pricing.extraFees}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ name: '', amount: 0, frequency: 'once' })}
                      fields={extraFeesFieldValues} />
                  </div>
                </Collapsible>

                {/* <h3>Virtual Tour</h3>
                <TextInputField
                  fieldType="text"
                  fieldLabel="Virtual Tour"
                  placeholder="Virtual Tour"
                  fieldName={`virtualTour`} /> */}

                <h3>📢 Public Property</h3>
                <CheckBoxInputField
                  fieldLabel={'Display this listing to the public'}
                  fieldName={'public'} />

                <h3>🦺 Services</h3>
                <CheckBoxInputField
                  fieldLabel={'This property requires cleaning services'}
                  fieldName={'services.cleaning'} />

                <CheckBoxInputField
                  fieldLabel={'This property requires additional cleaning services'}
                  fieldName={'services.additionalCleaning'} />

                <CheckBoxInputField
                  fieldLabel={'This property requires booking guest info services'}
                  fieldName={'services.bookingGuestInfo'} />

                <CheckBoxInputField
                  fieldLabel={'This property requires booking guest arrival time'}
                  fieldName={'services.bookingGuestArrivalTime'} />

                <CheckBoxInputField
                  fieldLabel={'This property requires identification with the booking guest information'}
                  fieldName={'services.bookingGuestId'} />

                <CheckBoxInputField
                  fieldLabel={'Automatically send booking guest info to the booking guest email'}
                  fieldName={'services.autoSendBookingGuestInfo'} />

                <TextInputField
                  fieldType="text"
                  fieldLabel="Booking Guest Email"
                  placeholder="checkerinner@checkin.com"
                  fieldName={`services.bookingGuestEmail`} />

                <hr />

                <h3>Platform Settings</h3>

                <CheckBoxInputField
                  fieldLabel={'VRBO: This property has VRBO integration with quote hold enabled'}
                  fieldName={'platformSettings.vrboIntegration.quoteHold'} />


                <hr />

                {/* <h3>Linked Services</h3> */}
                <Collapsible
                  title="🤖 Linked Services"
                  TitleTag="h3"
                >
                  <div>
                    <label htmlFor="Linked Services">Linked Services</label><br />

                    {[
                      { linkedServiceName: 'Fontaine Portal', linkedService: 'fontainePortal', linkedServicePayloadFn: fontainePortalLinkedService },
                      { linkedServiceName: 'Fontaine Property', linkedService: 'fontaine', linkedServicePayloadFn: fontainePropertyLinkedService },
                      { linkedServiceName: 'Blocking Property', linkedService: 'blockingProperty', linkedServicePayloadFn: blockingLinkedService },
                      { linkedServiceName: 'Early Check In', linkedService: 'earlyCheckIn', linkedServicePayloadFn: earlyCheckInLinkedService },
                      { linkedServiceName: 'Late Check Out', linkedService: 'lateCheckOut', linkedServicePayloadFn: lateCheckOutLinkedService },
                      { linkedServiceName: 'Remote Lock', linkedService: 'remoteLock', linkedServicePayloadFn: remoteLockLinkedService },
                      { linkedServiceName: 'Parking', linkedService: 'parking', linkedServicePayloadFn: parkingLinkedService },
                      { linkedServiceName: 'Gate Code', linkedService: 'gateCode', linkedServicePayloadFn: gateCodeLinkedService },
                    ].map((linkedService: any, idx: number) => {
                      let isEligible = eligible?.linkedServices?.[linkedService.linkedService]
                      if (!isEligible) {
                        return null
                      }
                      return (
                        <Button
                          key={`add-linked-service-btn-${idx}`}
                          className={"me-2 mb-2"}
                          variant={"secondary"}
                          size={"sm"}
                          onClick={() => {
                            setFieldValue(
                              'linkedServices',
                              [
                                ...values.linkedServices,
                                linkedService.linkedServicePayloadFn()
                              ]
                            )
                          }}
                        >
                          + {linkedService.linkedServiceName}
                        </Button>
                      )
                    })}

                    {/* edit linked service buttons */}{[
                      { linkedServiceName: 'Early Check In', linkedService: 'earlyCheckIn' },
                      { linkedServiceName: 'Late Check Out', linkedService: 'lateCheckOut' },
                      { linkedServiceName: 'Remote Lock', linkedService: 'remoteLock' },
                      { linkedServiceName: 'Fontaine Portal', linkedService: 'fontainePortal' },
                      // { linkedServiceName: 'Blocking Property', linkedService: 'blockingProperty' },
                      { linkedServiceName: 'Parking', linkedService: 'parking' },
                      { linkedServiceName: 'Gate Code', linkedService: 'gateCode' },
                    ].map((linkedService: any, idx: number) => {
                      const existingIndex = eligible?.linkedServiceIndexes?.[linkedService.linkedService]
                      if (existingIndex === undefined || existingIndex === -1) {
                        return null
                      }
                      return (
                        <Button
                          key={`edit-linked-service-btn-${idx}`}
                          className={"me-2 mb-2"}
                          variant={"outline-primary"}
                          size={"sm"}
                          onClick={() => {
                            setLinkedServiceToEdit({
                              index: existingIndex,
                              payload: values.linkedServices[existingIndex],
                              title: `Edit ${linkedService.linkedServiceName}`
                            })
                            setEditLinkedServiceModalOpen(true)
                          }}
                        >
                          Edit {linkedService.linkedServiceName}
                        </Button>
                      )
                    })}

                    <ArrayFieldInput
                      fieldName={'linkedServices'}
                      values={values.linkedServices}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ linkedService: '', linkedServiceId: '' })}
                      fields={linkedServicesFieldValues} />
                  </div>
                </Collapsible>


                <DateInputField
                  fieldLabel={'Date property was added'}
                  placeholder={'mm/dd/yyyy'}
                  fieldName={'created'} />

                {/*Props: {JSON.stringify(props)}
            <br />*/}
                Errors: {JSON.stringify(errors)}
                <br />
                <Button
                  className="mb-3"
                  type="submit"
                  disabled={Object.keys(errors).length > 0 || isSubmitting || disabled || uploadingImage}
                >
                  Submit
                </Button>
              </Form>
              <SelectPropertyModal
                active={copyPropertySettingsModal !== ''}
                onClose={() => setCopyPropertySettingsModal('')}
                onSelect={(selectedProperty: any) => {
                  console.log(selectedProperty)
                  const guestInstruction = checkVal(selectedProperty, copyPropertySettingsModal)
                  // const guestInstruction = selectedProperty.about?.guestInstructions
                  if (guestInstruction) {
                    setFieldValue(copyPropertySettingsModal, guestInstruction)
                  }
                }}
              />
              <FindCoordinatesModal
                active={findCoordinatesModalOpen}
                onClose={() => setFindCoordinatesModalOpen(false)}
                address={values.address}
                onCoordinatesFound={(coordinates) => {
                  setFieldValue('coordinates', coordinates)
                  setFindCoordinatesModalOpen(false)
                }}
              />
              <EditLinkedServiceModal
                active={editLinkedServiceModalOpen}
                onClose={() => setEditLinkedServiceModalOpen(false)}
                linkedServiceData={linkedServiceToEdit.payload}
                onSave={(updatedLinkedServiceData: any) => {
                  let updatedLinkedServices: any[] = [...values.linkedServices]
                  updatedLinkedServices[linkedServiceToEdit.index] = updatedLinkedServiceData
                  setFieldValue('linkedServices', updatedLinkedServices)
                  setEditLinkedServiceModalOpen(false)
                }}
                title={linkedServiceToEdit.title ? linkedServiceToEdit.title : 'Edit Linked Service'}
                linkedServiceType="property"
              />
            </>
          )
        }}
      </Formik>
    </div>
  )
}